import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { weeklyScheduleAdminApi } from '../../services/api'
import { PageSlugs } from '../../constants/pageSlugs'
import Button from '../../components/ui/Button'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'

const EMPTY_DAY = () => ({
  day_en: '',
  day_ar: '',
  date_label: '',
  note_en: '',
  note_ar: '',
  sort_order: 0,
})

export default function AdminWeeklySchedule() {
  const { t } = useTranslation()
  const [form, setForm] = useState(null)
  const [activeWeek, setActiveWeek] = useState(0)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const load = () => {
    setLoading(true)
    setError('')
    weeklyScheduleAdminApi
      .get(PageSlugs.weeklySchedule412)
      .then(({ data }) => {
        const schedule = data.data || data
        setForm({
          ...schedule,
          weeks: (schedule.weeks || []).map((week, index) => ({
            ...week,
            sort_order: week.sort_order ?? index,
            days: (week.days || []).map((day, dayIndex) => ({
              ...day,
              sort_order: day.sort_order ?? dayIndex,
            })),
          })),
        })
        setActiveWeek(0)
      })
      .catch(() => setError(t('admin.weeklySchedule.loadError')))
      .finally(() => setLoading(false))
  }

  useEffect(load, [t])

  const updateMeta = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const updateWeek = (weekIndex, key, value) => {
    setForm((prev) => {
      const weeks = prev.weeks.map((week, index) =>
        index === weekIndex ? { ...week, [key]: value } : week
      )
      return { ...prev, weeks }
    })
  }

  const updateDay = (weekIndex, dayIndex, key, value) => {
    setForm((prev) => {
      const weeks = prev.weeks.map((week, index) => {
        if (index !== weekIndex) return week
        const days = week.days.map((day, dIndex) =>
          dIndex === dayIndex ? { ...day, [key]: value } : day
        )
        return { ...week, days }
      })
      return { ...prev, weeks }
    })
  }

  const addDay = (weekIndex) => {
    setForm((prev) => {
      const weeks = prev.weeks.map((week, index) => {
        if (index !== weekIndex) return week
        return {
          ...week,
          days: [...week.days, { ...EMPTY_DAY(), sort_order: week.days.length }],
        }
      })
      return { ...prev, weeks }
    })
  }

  const removeDay = (weekIndex, dayIndex) => {
    setForm((prev) => {
      const weeks = prev.weeks.map((week, index) => {
        if (index !== weekIndex) return week
        return { ...week, days: week.days.filter((_, dIndex) => dIndex !== dayIndex) }
      })
      return { ...prev, weeks }
    })
  }

  const addWeek = () => {
    setForm((prev) => {
      const nextNumber = Math.max(0, ...prev.weeks.map((w) => w.week_number)) + 1
      const weeks = [
        ...prev.weeks,
        {
          week_number: nextNumber,
          title_en: `Week ${nextNumber}`,
          title_ar: `الأسبوع ${nextNumber}`,
          sort_order: prev.weeks.length,
          days: [
            { day_en: 'Sat', day_ar: 'السبت', date_label: '', note_en: '', note_ar: '', sort_order: 0 },
            { day_en: 'Sun', day_ar: 'الأحد', date_label: '', note_en: '', note_ar: '', sort_order: 1 },
            { day_en: 'Mon', day_ar: 'الاثنين', date_label: '', note_en: '', note_ar: '', sort_order: 2 },
            { day_en: 'Tue', day_ar: 'الثلاثاء', date_label: '', note_en: '', note_ar: '', sort_order: 3 },
            { day_en: 'Wed', day_ar: 'الأربعاء', date_label: '', note_en: '', note_ar: '', sort_order: 4 },
            { day_en: 'Thu', day_ar: 'الخميس', date_label: '', note_en: '', note_ar: '', sort_order: 5 },
          ],
        },
      ]
      setActiveWeek(weeks.length - 1)
      return { ...prev, weeks }
    })
  }

  const removeWeek = (weekIndex) => {
    if (!window.confirm(t('admin.weeklySchedule.confirmDeleteWeek'))) return
    setForm((prev) => {
      const weeks = prev.weeks.filter((_, index) => index !== weekIndex)
      setActiveWeek((current) => Math.min(current, Math.max(0, weeks.length - 1)))
      return { ...prev, weeks }
    })
  }

  const handleSave = async (event) => {
    event.preventDefault()
    if (!form) return
    setSaving(true)
    setError('')
    setSuccess('')
    try {
      const payload = {
        title_en: form.title_en,
        title_ar: form.title_ar,
        subtitle_en: form.subtitle_en || null,
        subtitle_ar: form.subtitle_ar || null,
        tagline_en: form.tagline_en || null,
        tagline_ar: form.tagline_ar || null,
        sidebar_quote_en: form.sidebar_quote_en || null,
        sidebar_quote_ar: form.sidebar_quote_ar || null,
        footer_tagline_en: form.footer_tagline_en || null,
        footer_tagline_ar: form.footer_tagline_ar || null,
        is_active: form.is_active !== false,
        weeks: form.weeks.map((week, index) => ({
          id: week.id,
          week_number: Number(week.week_number),
          title_en: week.title_en,
          title_ar: week.title_ar,
          sort_order: week.sort_order ?? index,
          days: week.days.map((day, dayIndex) => ({
            id: day.id,
            sort_order: day.sort_order ?? dayIndex,
            day_en: day.day_en,
            day_ar: day.day_ar,
            date_label: day.date_label || null,
            note_en: day.note_en || null,
            note_ar: day.note_ar || null,
          })),
        })),
      }
      const { data } = await weeklyScheduleAdminApi.update(PageSlugs.weeklySchedule412, payload)
      const schedule = data.data || data
      setForm({
        ...schedule,
        weeks: schedule.weeks || [],
      })
      setSuccess(t('admin.weeklySchedule.saved'))
    } catch (err) {
      setError(err?.response?.data?.message || t('admin.weeklySchedule.saveError'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingSpinner className="py-20" />
  if (!form) {
    return (
      <div>
        <h1 className="mb-4 text-2xl font-bold text-primary">{t('admin.weeklySchedule.title')}</h1>
        <p className="text-red-600">{error || t('admin.weeklySchedule.loadError')}</p>
      </div>
    )
  }

  const week = form.weeks[activeWeek]

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t('admin.weeklySchedule.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('admin.weeklySchedule.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" type="button" onClick={addWeek}>
            <Plus className="me-1 h-4 w-4" />
            {t('admin.weeklySchedule.addWeek')}
          </Button>
          <Button variant="primary" size="sm" type="submit" form="weekly-schedule-form" disabled={saving}>
            {saving ? t('admin.weeklySchedule.saving') : t('buttons.save')}
          </Button>
        </div>
      </div>

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}
      {success ? <p className="mb-4 text-sm text-green-700">{success}</p> : null}

      <form id="weekly-schedule-form" onSubmit={handleSave} className="space-y-6">
        <Card>
          <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.weeklySchedule.pageContent')}</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <AdminFormField label={t('admin.weeklySchedule.titleEn')}>
              <input className={adminInputClass} value={form.title_en || ''} onChange={(e) => updateMeta('title_en', e.target.value)} required />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.titleAr')}>
              <input className={adminInputClass} dir="rtl" value={form.title_ar || ''} onChange={(e) => updateMeta('title_ar', e.target.value)} required />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.subtitleEn')}>
              <input className={adminInputClass} value={form.subtitle_en || ''} onChange={(e) => updateMeta('subtitle_en', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.subtitleAr')}>
              <input className={adminInputClass} dir="rtl" value={form.subtitle_ar || ''} onChange={(e) => updateMeta('subtitle_ar', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.taglineEn')}>
              <input className={adminInputClass} value={form.tagline_en || ''} onChange={(e) => updateMeta('tagline_en', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.taglineAr')}>
              <input className={adminInputClass} dir="rtl" value={form.tagline_ar || ''} onChange={(e) => updateMeta('tagline_ar', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.sidebarQuoteEn')}>
              <input className={adminInputClass} value={form.sidebar_quote_en || ''} onChange={(e) => updateMeta('sidebar_quote_en', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.sidebarQuoteAr')}>
              <input className={adminInputClass} dir="rtl" value={form.sidebar_quote_ar || ''} onChange={(e) => updateMeta('sidebar_quote_ar', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.footerTaglineEn')}>
              <input className={adminInputClass} value={form.footer_tagline_en || ''} onChange={(e) => updateMeta('footer_tagline_en', e.target.value)} />
            </AdminFormField>
            <AdminFormField label={t('admin.weeklySchedule.footerTaglineAr')}>
              <input className={adminInputClass} dir="rtl" value={form.footer_tagline_ar || ''} onChange={(e) => updateMeta('footer_tagline_ar', e.target.value)} />
            </AdminFormField>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.is_active !== false}
              onChange={(e) => updateMeta('is_active', e.target.checked)}
            />
            {t('admin.weeklySchedule.isActive')}
          </label>
        </Card>

        <Card>
          <div className="mb-4 flex flex-wrap gap-2">
            {form.weeks.map((item, index) => (
              <button
                key={item.id || `week-${index}`}
                type="button"
                onClick={() => setActiveWeek(index)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  index === activeWeek
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {item.title_en || `Week ${item.week_number}`}
              </button>
            ))}
          </div>

          {week ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="grid flex-1 gap-4 md:grid-cols-3">
                  <AdminFormField label={t('admin.weeklySchedule.weekNumber')}>
                    <input
                      type="number"
                      min={1}
                      className={adminInputClass}
                      value={week.week_number}
                      onChange={(e) => updateWeek(activeWeek, 'week_number', e.target.value)}
                      required
                    />
                  </AdminFormField>
                  <AdminFormField label={t('admin.weeklySchedule.weekTitleEn')}>
                    <input
                      className={adminInputClass}
                      value={week.title_en || ''}
                      onChange={(e) => updateWeek(activeWeek, 'title_en', e.target.value)}
                      required
                    />
                  </AdminFormField>
                  <AdminFormField label={t('admin.weeklySchedule.weekTitleAr')}>
                    <input
                      className={adminInputClass}
                      dir="rtl"
                      value={week.title_ar || ''}
                      onChange={(e) => updateWeek(activeWeek, 'title_ar', e.target.value)}
                      required
                    />
                  </AdminFormField>
                </div>
                <Button variant="outline" size="sm" type="button" onClick={() => removeWeek(activeWeek)}>
                  <Trash2 className="me-1 h-4 w-4" />
                  {t('admin.weeklySchedule.deleteWeek')}
                </Button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b text-start text-gray-600">
                      <th className="px-2 py-2 font-medium">{t('admin.weeklySchedule.dayEn')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.weeklySchedule.dayAr')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.weeklySchedule.date')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.weeklySchedule.noteEn')}</th>
                      <th className="px-2 py-2 font-medium">{t('admin.weeklySchedule.noteAr')}</th>
                      <th className="px-2 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {week.days.map((day, dayIndex) => (
                      <tr key={day.id || `day-${dayIndex}`} className="border-b border-gray-100 align-top">
                        <td className="px-2 py-2">
                          <input
                            className={adminInputClass}
                            value={day.day_en || ''}
                            onChange={(e) => updateDay(activeWeek, dayIndex, 'day_en', e.target.value)}
                            required
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            className={adminInputClass}
                            dir="rtl"
                            value={day.day_ar || ''}
                            onChange={(e) => updateDay(activeWeek, dayIndex, 'day_ar', e.target.value)}
                            required
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            className={adminInputClass}
                            value={day.date_label || ''}
                            onChange={(e) => updateDay(activeWeek, dayIndex, 'date_label', e.target.value)}
                            placeholder="22/8"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            className={adminInputClass}
                            value={day.note_en || ''}
                            onChange={(e) => updateDay(activeWeek, dayIndex, 'note_en', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            className={adminInputClass}
                            dir="rtl"
                            value={day.note_ar || ''}
                            onChange={(e) => updateDay(activeWeek, dayIndex, 'note_ar', e.target.value)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            className="rounded p-1 text-red-600 hover:bg-red-50"
                            onClick={() => removeDay(activeWeek, dayIndex)}
                            aria-label={t('buttons.delete')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button variant="outline" size="sm" type="button" onClick={() => addDay(activeWeek)}>
                <Plus className="me-1 h-4 w-4" />
                {t('admin.weeklySchedule.addDay')}
              </Button>
            </div>
          ) : null}
        </Card>
      </form>
    </div>
  )
}
